import { useState, useEffect } from "react";

interface IProps {
	ref: HTMLElement | null;
	forceRerender?: number;
	trackOnResize?: boolean;
	skipTrackOnMutate?: boolean;
	trackOffsetHeight?: boolean;
}

const useTrackHeight = ({ ref, forceRerender, trackOnResize, skipTrackOnMutate = false, trackOffsetHeight }: IProps): { height: number } => {
	const [height, setHeight] = useState(0);

	useEffect(() => {
		// create a mutation observer to track the height of the element
		if (ref) {
			// initialize on load:
			const height = trackOffsetHeight
				? ref.offsetHeight || 0
				: ref.getBoundingClientRect()?.height || 0;
			setHeight(height);

			// listen for changes:
			const observer = new MutationObserver(() => {
				const _height = trackOffsetHeight
					? ref.offsetHeight || 0
					: ref.getBoundingClientRect()?.height || 0;
				setHeight(_height);
			});

			if (!skipTrackOnMutate) {
				observer.observe(ref, {
					attributes: true,
					characterData: true,
					childList: true,
					subtree: true,
				});
			}

			let frame: number | undefined;

			// listen for resize:
			const resizeObserver = new ResizeObserver((entries) => {
				if (frame) cancelAnimationFrame(frame);
				frame = requestAnimationFrame(() => {
					const [entry] = entries;
					// no need to call getBoundingClientRect here which forces a repaint because
					// the target element's resized height is already a part of the resize observer entries
					const borderBoxHeight = entry?.borderBoxSize?.[0]?.blockSize;
					setHeight(borderBoxHeight ?? entry?.contentRect?.height ?? 0);
				});
			});

			if (trackOnResize) {
				resizeObserver.observe(ref);
			}

			return () => {
				observer.disconnect();
				resizeObserver.disconnect();
			};
		}
	}, [ref, forceRerender, trackOnResize, skipTrackOnMutate, trackOffsetHeight]);

	return {
		height
	};
};

export default useTrackHeight;
